import React from 'react';
import { useTranslation } from 'react-i18next';
import { Job } from 'graphql-common';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import Typography from '@lib/components/Typography/Typography';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import { materialSymbolsOutlined } from '@lib/enums/common';
import JobStatusChip from '@lib/components/JobStatusChip/JobStatusChip';
import getJobTitle from 'components/JobTracker/utils/getJobTitle';
import getProgressDuration from 'components/JobTracker/utils/getProgressDuration';
import getJobIcon from 'components/JobTracker/utils/getJobIcon';
import useActions from 'components/JobTracker/utils/useActions';
import styles from './JobTracker.module.scss';

export default function JobPreview({
  job,
  removeJobId,
  restart,
  stop,
}: {
  job: Job;
  removeJobId: (jid: string) => void;
  restart: () => void;
  stop: () => void;
}) {
  const { t } = useTranslation();
  const title = getJobTitle(job.kind, t);
  const progressDuration = getProgressDuration(job.eta, t);
  const {
    onRemoveHandler,
    onRetryHandler,
    onCreateDeliveryRequestHandler,
    cancelJobMutationLoading,
    createDeliveryRequestMutationLoading,
    isCreateDeliveryRequestCalled,
    canRemoveJob,
    canCancelJob,
    canSendByEmail,
  } = useActions({
    job,
    removeJobId,
    restart,
    stop,
  });
  return (
    <div className={styles.job}>
      {getJobIcon(job.status)}
      <div className={styles.jobContent}>
        <div className={styles.jobTitleWrap}>
          <div className={styles.jobTitleRow}>
            <Typography variant="body" strong className={styles.jobTitle}>
              {title}
            </Typography>
            <JobStatusChip data={job} />
          </div>
        </div>
        <div className={styles.jobMessageWrap}>
          <Typography variant="caption" className={styles.jobMessage}>
            {!!progressDuration && (
              <span>
                {progressDuration}{' '}
                <span className={styles.durationSeparator}>|</span>{' '}
              </span>
            )}
            <span>{job.message}</span>
          </Typography>
        </div>
      </div>
      <div>
        {canSendByEmail && (
          <Button
            buttonType={ButtonTypes.primaryOutlined}
            buttonText={t('send-by-email')}
            leftIcon={
              job.deliveryRequest || isCreateDeliveryRequestCalled
                ? 'check'
                : 'mail'
            }
            leftIconClassName={materialSymbolsOutlined}
            onClick={onCreateDeliveryRequestHandler}
            disabled={!!job.deliveryRequest || isCreateDeliveryRequestCalled}
            isLoading={createDeliveryRequestMutationLoading}
          />
        )}
        {job.canRetry?.value && (
          <Button
            buttonType={ButtonTypes.primaryOutlined}
            buttonText={t('try-again')}
            leftIcon="refresh"
            leftIconClassName={materialSymbolsOutlined}
            onClick={onRetryHandler}
          />
        )}
      </div>
      <div>
        {(canRemoveJob || canCancelJob) && (
          <Tooltip body={canCancelJob ? t('cancel') : t('discard')}>
            <IconButton
              icon="cancel"
              type={IconButtonTypes.QuaternaryFilled}
              onClick={onRemoveHandler}
              rounded
              isLoading={cancelJobMutationLoading}
            />
          </Tooltip>
        )}
      </div>
    </div>
  );
}
